import { useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, Eye, Trash2 } from 'lucide-react';
import { useRentalBookings, useDeleteRentalBooking, useUpdateRentalBooking } from '@/hooks/useRentalBookings';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const AdminRentalBookings = () => {
  const { data: bookings, isLoading } = useRentalBookings();
  const deleteBooking = useDeleteRentalBooking();
  const updateBooking = useUpdateRentalBooking();
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<string | null>(null);

  const handleViewDetails = (booking: any) => {
    setSelectedBooking(booking);
    setDetailsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    setBookingToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (bookingToDelete) {
      deleteBooking.mutate(bookingToDelete);
      setDeleteDialogOpen(false);
      setBookingToDelete(null);
    }
  };

  const handleStatusChange = (id: string, status: string) => {
    updateBooking.mutate({ id, updates: { booking_status: status } });
  };

  const handlePaymentStatusChange = (id: string, status: string) => {
    updateBooking.mutate({ id, updates: { payment_status: status } });
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Rental Bookings</h1>
        </div>

        <div className="bg-card rounded-lg shadow">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Booking #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings && bookings.length > 0 ? (
                bookings.map((booking) => (
                  <TableRow key={booking.id}>
                    <TableCell className="font-medium">{booking.booking_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{booking.customer_name}</p>
                        <p className="text-sm text-muted-foreground">{booking.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{booking.vehicle_name}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{format(new Date(booking.pickup_date), 'MMM dd, yyyy')}</p>
                        <p className="text-muted-foreground">to</p>
                        <p>{format(new Date(booking.return_date), 'MMM dd, yyyy')}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold">${booking.total_price}</TableCell>
                    <TableCell>
                      <Select
                        value={booking.payment_status}
                        onValueChange={(value) => handlePaymentStatusChange(booking.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={booking.booking_status}
                        onValueChange={(value) => handleStatusChange(booking.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetails(booking)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(booking.id)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No bookings found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Booking Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Booking Details</DialogTitle>
            </DialogHeader>
            {selectedBooking && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">Booking Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Booking Number</p>
                      <p className="font-medium">{selectedBooking.booking_number}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Vehicle</p>
                      <p className="font-medium">{selectedBooking.vehicle_name}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Customer Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Name</p>
                      <p className="font-medium">{selectedBooking.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Email</p>
                      <p className="font-medium">{selectedBooking.email}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Phone</p>
                      <p className="font-medium">{selectedBooking.phone}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Rental Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Pickup</p>
                      <p className="font-medium">{selectedBooking.pickup_location}</p>
                      <p className="text-sm">
                        {format(new Date(selectedBooking.pickup_date), 'PPP')} at {selectedBooking.pickup_time}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Return</p>
                      <p className="font-medium">{selectedBooking.return_location}</p>
                      <p className="text-sm">
                        {format(new Date(selectedBooking.return_date), 'PPP')} at {selectedBooking.return_time}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Total Days</p>
                      <p className="font-medium">{selectedBooking.total_days} days</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Insurance</p>
                      <p className="font-medium capitalize">{selectedBooking.insurance_type}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Driver Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Age</p>
                      <p className="font-medium">{selectedBooking.driver_age} years</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">License Number</p>
                      <p className="font-medium">{selectedBooking.driver_license_number}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Additional Driver</p>
                      <p className="font-medium">{selectedBooking.additional_driver ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>

                {selectedBooking.extras && selectedBooking.extras.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Extras</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedBooking.extras.map((extra: string, index: number) => (
                        <Badge key={index} variant="secondary">
                          {extra}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="font-semibold mb-2">Pricing</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Rental ({selectedBooking.total_days} days x ${selectedBooking.daily_rate})</span>
                      <span>${selectedBooking.total_days * selectedBooking.daily_rate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Insurance</span>
                      <span>${selectedBooking.insurance_cost}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Extras</span>
                      <span>${selectedBooking.extras_cost}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-bold text-base">
                      <span>Total</span>
                      <span>${selectedBooking.total_price}</span>
                    </div>
                  </div>
                </div>

                {selectedBooking.remarks && (
                  <div>
                    <h3 className="font-semibold mb-2">Remarks</h3>
                    <p className="text-sm text-muted-foreground">{selectedBooking.remarks}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the booking.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminRentalBookings;